import type { FormatSize, Orientation } from "@/constants/imageFormats";

const GUEST_CREATE_DRAFT_KEY = "artify_guest_create_draft_v3";
const LEGACY_GUEST_CREATE_DRAFT_KEYS = [
  "artify_guest_create_draft_v2",
  "artify_guest_create_draft_v1",
] as const;
const GUEST_CREATE_DRAFT_VERSION = 3;
const MAX_DRAFT_AGE_MS = 24 * 60 * 60 * 1000;
const DATABASE_OPEN_TIMEOUT_MS = 500;
const DATABASE_TRANSACTION_TIMEOUT_MS = 1000;
const DRAFT_DATABASE_NAME = "artify_guest_create_drafts";
const DRAFT_STORE_NAME = "drafts";
const DRAFT_RECORD_ID = "active-create-draft";

interface GuestCreateDraftMetadata {
  version: typeof GUEST_CREATE_DRAFT_VERSION;
  draftId: string;
  savedAt: number;
  expiresAt: number;
  selectedOrientation: Orientation | null;
  selectedSize: FormatSize | null;
  selectedStyle: string;
  selectedSubStyle: string | null;
  hasImageSelection: boolean;
}

interface StoredGuestCreateDraft extends GuestCreateDraftMetadata {
  id: typeof DRAFT_RECORD_ID;
  imageBlob: Blob | null;
  imageName: string | null;
  imageType: string | null;
  imageLastModified: number | null;
}

export interface GuestCreateDraft extends GuestCreateDraftMetadata {
  selectedImage: File | null;
}

export interface GuestCreateDraftInput {
  selectedOrientation: Orientation | null;
  selectedSize: FormatSize | null;
  selectedStyle: string;
  selectedSubStyle: string | null;
  hasImageSelection: boolean;
  selectedImage?: File | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isOrientation(value: unknown): value is Orientation {
  return value === "portrait" || value === "landscape" || value === "square";
}

function isFormatSize(value: unknown): value is FormatSize {
  return value === "small" || value === "medium" || value === "large";
}

function createDraftId() {
  try {
    return globalThis.crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function parseMetadata(value: unknown): GuestCreateDraftMetadata | null {
  if (!isRecord(value)) return null;
  if (value.version !== 1 && value.version !== 2 && value.version !== GUEST_CREATE_DRAFT_VERSION) return null;
  if (typeof value.savedAt !== "number" || !Number.isFinite(value.savedAt) || value.savedAt > Date.now()) return null;

  const maximumExpiresAt = value.savedAt + MAX_DRAFT_AGE_MS;
  const expiresAt = typeof value.expiresAt === "number" && Number.isFinite(value.expiresAt)
    ? Math.min(value.expiresAt, maximumExpiresAt)
    : maximumExpiresAt;
  if (expiresAt <= Date.now()) return null;
  if (typeof value.selectedStyle !== "string") return null;

  return {
    version: GUEST_CREATE_DRAFT_VERSION,
    draftId: typeof value.draftId === "string" ? value.draftId : `legacy-${value.savedAt}`,
    savedAt: value.savedAt,
    expiresAt,
    selectedOrientation: isOrientation(value.selectedOrientation) ? value.selectedOrientation : null,
    selectedSize: isFormatSize(value.selectedSize) ? value.selectedSize : null,
    selectedStyle: value.selectedStyle,
    selectedSubStyle: typeof value.selectedSubStyle === "string" ? value.selectedSubStyle : null,
    hasImageSelection: value.hasImageSelection === true,
  };
}

function getStorage(name: "localStorage" | "sessionStorage"): Storage | null {
  try {
    return globalThis[name] ?? null;
  } catch {
    return null;
  }
}

function saveFallbackMetadata(metadata: GuestCreateDraftMetadata) {
  const serialized = JSON.stringify(metadata);
  try {
    const storage = getStorage("localStorage");
    if (storage) {
      storage.setItem(GUEST_CREATE_DRAFT_KEY, serialized);
      return;
    }
  } catch {
    // Fall through to tab-scoped storage.
  }

  try {
    getStorage("sessionStorage")?.setItem(GUEST_CREATE_DRAFT_KEY, serialized);
  } catch {
    // Authentication must remain available when browser storage is disabled.
  }
}

function takeFallbackMetadata(): GuestCreateDraftMetadata | null {
  let newest: GuestCreateDraftMetadata | null = null;

  for (const storageName of ["localStorage", "sessionStorage"] as const) {
    const storage = getStorage(storageName);
    if (!storage) continue;

    for (const key of [GUEST_CREATE_DRAFT_KEY, ...LEGACY_GUEST_CREATE_DRAFT_KEYS]) {
      try {
        const raw = storage.getItem(key);
        if (!raw) continue;
        storage.removeItem(key);
        const metadata = parseMetadata(JSON.parse(raw) as unknown);
        if (metadata && (!newest || metadata.savedAt > newest.savedAt)) newest = metadata;
      } catch {
        // Continue clearing and inspecting every other fallback candidate.
      }
    }
  }
  return newest;
}

function clearFallbackMetadata() {
  for (const storageName of ["localStorage", "sessionStorage"] as const) {
    try {
      const storage = getStorage(storageName);
      for (const key of [GUEST_CREATE_DRAFT_KEY, ...LEGACY_GUEST_CREATE_DRAFT_KEYS]) {
        storage?.removeItem(key);
      }
    } catch {
      // Best-effort cleanup only.
    }
  }
}

function openDraftDatabase(): Promise<IDBDatabase | null> {
  if (typeof globalThis.indexedDB === "undefined") return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (database: IDBDatabase | null) => {
      if (settled) {
        database?.close();
        return;
      }
      settled = true;
      globalThis.clearTimeout(timeout);
      resolve(database);
    };
    const timeout = globalThis.setTimeout(() => finish(null), DATABASE_OPEN_TIMEOUT_MS);

    try {
      const request = globalThis.indexedDB.open(DRAFT_DATABASE_NAME, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(DRAFT_STORE_NAME)) {
          request.result.createObjectStore(DRAFT_STORE_NAME, { keyPath: "id" });
        }
      };
      request.onsuccess = () => finish(request.result);
      request.onerror = () => finish(null);
      request.onblocked = () => finish(null);
    } catch {
      finish(null);
    }
  });
}

type StoredDraftWriteResult = "stored" | "stale" | "failed";

async function writeStoredDraft(draft: StoredGuestCreateDraft): Promise<StoredDraftWriteResult> {
  const database = await openDraftDatabase();
  if (!database) return "failed";

  return new Promise((resolve) => {
    let settled = false;
    let result: StoredDraftWriteResult = "failed";
    const finish = (outcome: StoredDraftWriteResult) => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timeout);
      database.close();
      resolve(outcome);
    };
    const transaction = database.transaction(DRAFT_STORE_NAME, "readwrite");
    const store = transaction.objectStore(DRAFT_STORE_NAME);
    const timeout = globalThis.setTimeout(() => {
      try { transaction.abort(); } catch { /* transaction already completed */ }
      finish("failed");
    }, DATABASE_TRANSACTION_TIMEOUT_MS);

    const request = store.get(DRAFT_RECORD_ID);
    request.onsuccess = () => {
      const current = request.result as StoredGuestCreateDraft | undefined;
      if (current && Number.isFinite(current.savedAt) && current.savedAt > draft.savedAt) {
        result = "stale";
        return;
      }
      store.put(draft);
      result = "stored";
    };
    transaction.oncomplete = () => finish(result);
    transaction.onerror = () => finish("failed");
    transaction.onabort = () => finish("failed");
  });
}

async function takeStoredDraft(): Promise<StoredGuestCreateDraft | null> {
  const database = await openDraftDatabase();
  if (!database) return null;

  return new Promise((resolve) => {
    let settled = false;
    let draft: StoredGuestCreateDraft | null = null;
    const finish = (value: StoredGuestCreateDraft | null) => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timeout);
      database.close();
      resolve(value);
    };
    const transaction = database.transaction(DRAFT_STORE_NAME, "readwrite");
    const store = transaction.objectStore(DRAFT_STORE_NAME);
    const request = store.get(DRAFT_RECORD_ID);
    const timeout = globalThis.setTimeout(() => {
      try { transaction.abort(); } catch { /* transaction already completed */ }
      finish(null);
    }, DATABASE_TRANSACTION_TIMEOUT_MS);

    request.onsuccess = () => {
      draft = (request.result as StoredGuestCreateDraft | undefined) ?? null;
      if (draft) store.delete(DRAFT_RECORD_ID);
    };
    transaction.oncomplete = () => finish(draft);
    transaction.onerror = () => finish(null);
    transaction.onabort = () => finish(null);
  });
}

async function clearStoredDraft(expectedDraftId?: string): Promise<void> {
  const database = await openDraftDatabase();
  if (!database) return;

  await new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timeout);
      database.close();
      resolve();
    };
    const transaction = database.transaction(DRAFT_STORE_NAME, "readwrite");
    const store = transaction.objectStore(DRAFT_STORE_NAME);
    const timeout = globalThis.setTimeout(() => {
      try { transaction.abort(); } catch { /* transaction already completed */ }
      finish();
    }, DATABASE_TRANSACTION_TIMEOUT_MS);

    if (!expectedDraftId) {
      store.delete(DRAFT_RECORD_ID);
    } else {
      const request = store.get(DRAFT_RECORD_ID);
      request.onsuccess = () => {
        const current = request.result as StoredGuestCreateDraft | undefined;
        if (current?.draftId === expectedDraftId) store.delete(DRAFT_RECORD_ID);
      };
    }
    transaction.oncomplete = finish;
    transaction.onerror = finish;
    transaction.onabort = finish;
  });
}

function scheduleExpiry(metadata: GuestCreateDraftMetadata) {
  if (typeof document === "undefined") return;
  const delay = Math.max(0, metadata.expiresAt - Date.now());
  globalThis.setTimeout(() => {
    void clearStoredDraft(metadata.draftId);
    const fallback = takeFallbackMetadata();
    if (fallback && fallback.draftId !== metadata.draftId) saveFallbackMetadata(fallback);
  }, delay);
}

export async function saveGuestCreateDraft(input: GuestCreateDraftInput): Promise<void> {
  const savedAt = Date.now();
  const metadata: GuestCreateDraftMetadata = {
    version: GUEST_CREATE_DRAFT_VERSION,
    draftId: createDraftId(),
    savedAt,
    expiresAt: savedAt + MAX_DRAFT_AGE_MS,
    selectedOrientation: input.selectedOrientation,
    selectedSize: input.selectedSize,
    selectedStyle: input.selectedStyle,
    selectedSubStyle: input.selectedSubStyle,
    hasImageSelection: !!input.selectedImage || input.hasImageSelection,
  };
  const image = input.selectedImage ?? null;

  const writeResult = await writeStoredDraft({
    id: DRAFT_RECORD_ID,
    ...metadata,
    imageBlob: image,
    imageName: image?.name ?? null,
    imageType: image?.type ?? null,
    imageLastModified: image?.lastModified ?? null,
  }).catch(() => "failed" as const);

  if (writeResult === "stored") {
    clearFallbackMetadata();
    scheduleExpiry(metadata);
  } else if (writeResult === "failed") {
    saveFallbackMetadata(metadata);
  }
}

export async function consumeGuestCreateDraft(): Promise<GuestCreateDraft | null> {
  const stored = await takeStoredDraft().catch(() => null);
  const storedMetadata = parseMetadata(stored);
  const fallbackMetadata = takeFallbackMetadata();
  const useStored = !!storedMetadata
    && (!fallbackMetadata || storedMetadata.savedAt >= fallbackMetadata.savedAt);
  const metadata = useStored ? storedMetadata : fallbackMetadata;

  if (!metadata) {
    await clearStoredDraft().catch(() => undefined);
    return null;
  }

  let selectedImage: File | null = null;
  if (useStored && stored?.imageBlob instanceof Blob && stored.imageName) {
    selectedImage = new File([stored.imageBlob], stored.imageName, {
      type: stored.imageType || stored.imageBlob.type,
      lastModified: stored.imageLastModified ?? metadata.savedAt,
    });
  }

  return {
    ...metadata,
    selectedImage,
  };
}
